import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../data/site";
import { PageHero, Reveal } from "../components/Shared";

export default function Gallery() {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (dir) => setActive((current) => (current === null ? null : (current + dir + GALLERY.length) % GALLERY.length)),
    []
  );

  useEffect(() => {
    if (active === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, step]);

  return (
    <>
      <PageHero
        eyebrow="Photo gallery"
        title="Sweet Moments at Ms. Jeanette's"
        sub="Snowballs, smiles, birthdays, and storybook corners — a peek at the memories being made."
        testid="gallery-hero"
        bg="bg-blue-50"
      />

      <section className="px-4 py-20 sm:px-6" data-testid="gallery-grid-section">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2">
          {GALLERY.map((image, i) => (
            <Reveal key={image.caption} delay={i * 0.06}>
              <button
                type="button"
                onClick={() => setActive(i)}
                data-testid={`gallery-thumb-${i}`}
                aria-label={`Open photo: ${image.caption}`}
                className="group block w-full overflow-hidden rounded-3xl shadow-lg shadow-blue-500/10 focus:outline-none focus:ring-4 focus:ring-blue-300"
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <span className="block bg-white px-4 py-3 text-left text-sm font-extrabold text-slate-700">{image.caption}</span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/85 p-4"
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-label="Photo lightbox"
            data-testid="gallery-lightbox"
          >
            <button
              type="button"
              onClick={close}
              aria-label="Close photo"
              data-testid="lightbox-close-btn"
              className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 focus:outline-none focus:ring-4 focus:ring-yellow-300"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous photo"
              data-testid="lightbox-prev-btn"
              className="absolute left-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 focus:outline-none focus:ring-4 focus:ring-yellow-300"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next photo"
              data-testid="lightbox-next-btn"
              className="absolute right-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 focus:outline-none focus:ring-4 focus:ring-yellow-300"
            >
              <ChevronRight className="h-6 w-6" aria-hidden="true" />
            </button>
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[active].url}
                alt={GALLERY[active].alt}
                className="max-h-[78vh] w-auto max-w-full rounded-2xl object-contain"
                data-testid="lightbox-image"
              />
              <figcaption className="mt-3 text-center text-sm font-extrabold text-white" data-testid="lightbox-caption">
                {GALLERY[active].caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
